import type { PatientResponse } from '../services/api/types.gen';

/**
 * The address parts a map link is built from. Typed as a slice of the patient
 * record so callers can pass the patient straight through, which keeps a
 * street/city/state/zip mix-up from being expressible at the call site.
 */
export type MappableAddress = Pick<
  PatientResponse,
  'address' | 'city' | 'state' | 'zip_code'
>;

/**
 * Builds a Google Maps search URL for a patient address, or null when there is
 * no street address to search for.
 *
 * Uses the documented Google Maps URL form, which resolves to the Maps app on
 * Android, to Maps or the browser on iOS, and to the web map on desktop. City,
 * state and ZIP are appended when present so a bare street name does not match
 * the wrong town, but a street on its own is still worth linking.
 */
export function buildGoogleMapsUrl(location: MappableAddress): string | null {
  const street = location.address?.trim();
  if (!street) return null;

  const city = location.city?.trim();
  const state = location.state?.trim();
  const zip = location.zip_code?.trim();

  // State and ZIP read as one locality token ("AZ 85001"), so they join with a
  // space and the coarser parts join with commas.
  const region = [state, zip].filter(Boolean).join(' ');
  const query = [street, city, region].filter(Boolean).join(', ');

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
